import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Headphones, RefreshCw, Users, Phone, AlertTriangle, X } from "lucide-react";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader, { TabConfig } from "../../components/PageHeader";
import NewLeadsPool from "./NewLeadsPool";
import MyLeads from "./MyLeads";
import LeadEditPage from "./LeadEditPage";
import LeadProcessingPage from "./LeadProcessingPage";
import { Lead } from "../../types";
import { API_BASE_URL } from "../../utils/apiConfig";
import { useLanguage } from "../../contexts/LanguageContext";

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  countries?: string[];
  permissions?: string[];
  achievements?: string[]; // Task achievements for call operators
}

interface CallCenterMainProps {
  currentUser: User;
}

type TabType = "new-leads" | "my-leads" | "next-call" | "leads" | "my-leads-all";

const CallCenterMain: React.FC<CallCenterMainProps> = ({ currentUser }) => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const initialTab: TabType =
    tabFromUrl === "my-leads" || tabFromUrl === "new-leads" ? tabFromUrl : "new-leads";
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newLeads, setNewLeads] = useState<any[]>([]); // Changed to any[] to support both leads and orders during transition
  const [newLeadsTotal, setNewLeadsTotal] = useState(0);
  const [newLeadsCountries, setNewLeadsCountries] = useState<string[]>([]);
  const [newLeadsOperators, setNewLeadsOperators] = useState<Array<{ userId: string; username: string }>>([]);
  const [myLeads, setMyLeads] = useState<Lead[]>([]);
  const [myLeadsTotal, setMyLeadsTotal] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [editingLeadId, setEditingLeadId] = useState<number | null>(null);
  const [processingLeadId, setProcessingLeadId] = useState<string | null>(null);
  const [activeCallLeadId, setActiveCallLeadId] = useState<string | null>(null); // Changed to leadId (string)
  // When opened from Leads (Edit → processLead=id); on back we do NOT release so lead stays in My Leads
  const [openedFromLeadsLeadId, setOpenedFromLeadsLeadId] = useState<string | null>(null);

  // When navigated from Leads with ?processLead=id, open Lead Processing and clear the param
  const processLeadFromUrl = searchParams.get("processLead");
  useEffect(() => {
    if (processLeadFromUrl) {
      setProcessingLeadId(processLeadFromUrl);
      setOpenedFromLeadsLeadId(processLeadFromUrl);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("processLead");
        return next;
      }, { replace: true });
    }
  }, [processLeadFromUrl, setSearchParams]);

  // Sync activeTab with URL ?tab= (e.g. /call-center?tab=my-leads)
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t === "my-leads" || t === "new-leads") {
      setActiveTab(t as TabType);
    }
  }, [searchParams]);

  // Fetch available countries for new leads (from leads sent to call center)
  const fetchNewLeadsCountries = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}/api/leads/call-center/available?page=1&limit=1000`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setNewLeadsCountries(data.data.countries || []);
      }
    } catch (err) {
      console.error("Error fetching countries:", err);
    }
  }, []);

  // Fetch new leads with pagination and filters (leads sent to call center)
  const fetchNewLeads = useCallback(async (
    page: number = 1,
    limit: number = 50,
    search: string = "",
    task: string = "all",
    lastOperatorId: string = ""
  ) => {
    try {
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) params.append("search", search);
      if (task && task !== "all") params.append("task", task);
      if (lastOperatorId) params.append("lastOperatorId", lastOperatorId);

      const response = await fetch(
        `${API_BASE_URL}/api/leads/call-center/available?${params.toString()}`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setNewLeads(data.data.leads || []);
        setNewLeadsTotal(data.data.pagination?.totalCount ?? 0);
        setNewLeadsOperators(data.data.operators || []);
        if (data.data.countries) {
          setNewLeadsCountries(data.data.countries || []);
        } else {
          await fetchNewLeadsCountries();
        }
      } else {
        throw new Error(data.error || "Failed to fetch new leads");
      }
    } catch (err) {
      console.error("Error fetching new leads:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch new leads");
    }
  }, [fetchNewLeadsCountries]);

  // Fetch my leads with pagination and filters (leads assigned to current operator)
  const fetchMyLeads = useCallback(async (
    page: number = 1,
    limit: number = 50,
    search: string = "",
    statusFilter: string = ""
  ) => {
    try {
      const token = localStorage.getItem("authToken");
      
      const params = new URLSearchParams({
        leadOwnerId: currentUser.id, // Filter by leadOwner
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) params.append("search", search);
      // Note: statusFilter is for orders, not leads, so we ignore it for leads

      const response = await fetch(
        `${API_BASE_URL}/api/leads?${params.toString()}`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setMyLeads(data.data.leads || []);
        // totalCount is in pagination object
        setMyLeadsTotal(data.data.pagination?.totalCount || data.data.leads?.length || 0);
      } else {
        throw new Error(data.error || "Failed to fetch my leads");
      }
    } catch (err) {
      console.error("Error fetching my leads:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch my leads");
    }
  }, [currentUser.id]);

  // Helper function to check permissions
  const hasPermission = useCallback((permission: string): boolean => {
    return currentUser.permissions?.includes(permission) || false;
  }, [currentUser.permissions]);

  // Check if user is a call-center operator (call-center or call-operator role)
  const isCallCenterOperator = currentUser.role === "call-center" || currentUser.role === "call-operator";
  const isAdmin = currentUser.role === "admin" || currentUser.role === "super-admin";

  // Permission checks - admins have full access
  const canViewCallCenter = isAdmin || hasPermission("call-center.view");
  const canViewNewLeads = isAdmin || hasPermission("call-center.view") || hasPermission("call-center.leads.view");
  const canViewMyLeads = isAdmin || hasPermission("call-center.view") || hasPermission("call-center.leads.view");
  const canTakeLeads = isAdmin || hasPermission("call-center.leads.take") || hasPermission("orders.take_control");
  const canEditLeads = isAdmin || hasPermission("call-center.leads.edit") || hasPermission("orders.edit");

  // Initial data load (only if user has permissions)
  useEffect(() => {
    if (!canViewCallCenter) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const promises = [];
        if (canViewNewLeads) {
          promises.push(fetchNewLeads(1, 50, "", "all"));
        }
        if (canViewMyLeads) {
          promises.push(fetchMyLeads(1, 1000, "", "all"));
        }
        await Promise.all(promises);
      } catch (err) {
        console.error("Error loading call center data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [fetchNewLeads, fetchMyLeads, canViewCallCenter, canViewNewLeads, canViewMyLeads]);

  // Refresh data (only for tabs user has access to)
  const handleRefresh = useCallback(async () => {
    if (!canViewCallCenter) return;

    setRefreshing(true);
    setError(null);

    try {
      const promises = [];
      if (canViewNewLeads) {
        promises.push(fetchNewLeads());
      }
      if (canViewMyLeads) {
        promises.push(fetchMyLeads());
      }
      await Promise.all(promises);
    } catch (err) {
      console.error("Error refreshing data:", err);
    } finally {
      setRefreshing(false);
    }
  }, [fetchNewLeads, fetchMyLeads, canViewCallCenter, canViewNewLeads, canViewMyLeads]);

  // Take next lead from pool (atomic: only one operator gets it)
  const handleTakeNextFromPool = useCallback(async () => {
    if (!canTakeLeads) {
      setError("You don't have permission to take leads");
      return;
    }
    if (activeCallLeadId) {
      setError("Finish your current call before taking another lead.");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}/api/leads/call-center/take-next`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 404 || (data && data.error && data.error.includes('No available'))) {
          setError("No available lead in pool. Try again later.");
          return;
        }
        throw new Error(data.error || "Failed to take lead");
      }
      if (!data.success || !data.data?.lead) {
        setError("No available lead in pool.");
        return;
      }
      const leadId = data.data.lead.id;
      setActiveCallLeadId(leadId);
      setProcessingLeadId(leadId);
      setError(null);
      await Promise.all([
        fetchNewLeads(1, 50, "", "all"),
        fetchMyLeads(1, 1000, "", "all"),
      ]);
    } catch (err) {
      console.error("Error taking next lead:", err);
      setError(err instanceof Error ? err.message : "Failed to take lead");
      throw err;
    }
  }, [canTakeLeads, activeCallLeadId, fetchNewLeads, fetchMyLeads]);

  // Used by handleTakeNextLead when source is "pool" (end-call + next call flow)
  const handleTakeLead = useCallback(
    async (leadId: string) => {
      if (!canTakeLeads) return;
      try {
        const token = localStorage.getItem("authToken");
        const updatePayload = {
          leadOwner: {
            userId: currentUser.id,
            username: currentUser.username,
            email: currentUser.email,
            role: currentUser.role,
            timestamp: new Date().toISOString(),
          }
        };
        const response = await fetch(
          `${API_BASE_URL}/api/leads/${leadId}`,
          { method: "PUT", credentials: "include", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(updatePayload) }
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Failed to take lead");
        setActiveCallLeadId(leadId);
        setProcessingLeadId(leadId);
        await Promise.all([fetchNewLeads(1, 50, "", "all"), fetchMyLeads(1, 1000, "", "all")]);
      } catch (err) {
        console.error("Error taking lead:", err);
        setError(err instanceof Error ? err.message : "Failed to take lead");
        throw err;
      }
    },
    [fetchNewLeads, fetchMyLeads, canTakeLeads, currentUser]
  );

  // Handle back from lead-processing: return to My Leads tab (and refresh).
  const handleBackFromEdit = useCallback(() => {
    setOpenedFromLeadsLeadId(null);
    setEditingLeadId(null);
    setProcessingLeadId(null);
    setActiveCallLeadId(null);
    setActiveTab("my-leads");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", "my-leads");
      return next;
    }, { replace: true });
    fetchNewLeads(1, 50, "", "all");
    fetchMyLeads(1, 1000, "", "all");
  }, [fetchNewLeads, fetchMyLeads, setSearchParams]);

  // Automatically take the next lead: first from My Leads (by priority + time rules), then from pool
  const handleTakeNextLead = useCallback(async (excludeLeadId?: string) => {
    if (!canTakeLeads) {
      console.log("No permission to take leads");
      handleBackFromEdit();
      return;
    }

    try {
      // Small delay so DB has the updated lead (e.g. after Save & Next Call)
      await new Promise(resolve => setTimeout(resolve, 300));

      const token = localStorage.getItem("authToken");
      const url = excludeLeadId
        ? `${API_BASE_URL}/api/leads/call-center/next-lead?excludeLeadId=${encodeURIComponent(excludeLeadId)}`
        : `${API_BASE_URL}/api/leads/call-center/next-lead`;
      const response = await fetch(url, {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        handleBackFromEdit();
        return;
      }

      const data = await response.json();
      if (!data.success || !data.data?.lead) {
        handleBackFromEdit();
        return;
      }

      const { lead, source } = data.data;
      if (source === "my_leads") {
        // Already assigned to me; just navigate to it
        setActiveCallLeadId(lead.id);
        setProcessingLeadId(lead.id);
        await Promise.all([
          fetchNewLeads(1, 50, "", "all"),
          fetchMyLeads(1, 1000, "", "all"),
        ]);
        return;
      }
      if (source === "pool") {
        await handleTakeLead(lead.id);
        return;
      }

      handleBackFromEdit();
    } catch (err) {
      console.error("Error taking next lead:", err);
      handleBackFromEdit();
    }
  }, [canTakeLeads, handleTakeLead, handleBackFromEdit, fetchNewLeads, fetchMyLeads]);

  // Open lead for editing / Call Again (from My Leads tab)
  const handleEditLead = useCallback(async (leadId: string) => {
    if (!canEditLeads) {
      setError("You don't have permission to edit leads");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      const belgradeNow = new Date().toLocaleString("en-CA", {
        timeZone: "Europe/Belgrade",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
      await fetch(`${API_BASE_URL}/api/leads/${leadId}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          callAgainClickedAt: belgradeNow,
          callAgainClickedAtTimezone: "Europe/Belgrade",
          callAgainClickedBy: {
            userId: currentUser.id,
            username: currentUser.username,
          },
        }),
      });
    } catch (e) {
      console.error("Error recording Call Again click:", e);
    }
    setActiveCallLeadId(leadId);
    setProcessingLeadId(leadId);
  }, [canEditLeads, currentUser.id, currentUser.username]);

  // Tab configuration with permission checks
  // Hide "Leads" and "Next Call" tabs as requested
  const tabs: TabConfig[] = [
    // Available Leads Pool - requires call-center.view or call-center.leads.view
    ...(canViewNewLeads ? [{
      id: "new-leads" as TabType,
      label: t('leads.availableLeadsPool'),
      icon: Users,
    }] : []),
    // My Orders - requires call-center.view or call-center.leads.view
    ...(canViewMyLeads ? [{
      id: "my-leads" as TabType,
      label: t('callCenter.myLeads'),
      icon: Headphones,
    }] : []),
    // Tab 3: "My Leads" for call-center operators
  ];

  // No global header buttons; Refresh is only on My Leads tab
  const customButtons: Array<{ id: string; label: string; icon: typeof RefreshCw; onClick: () => void; variant?: "secondary"; disabled?: boolean }> = [];

  // When on Available Pool tab, loading is shown inside the NewLeadsPool card — avoid full-page spinner
  if (loading && activeTab !== "new-leads") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Permission check - redirect if no access
  if (!canViewCallCenter) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {t('error.forbidden')}
          </h3>
          <p className="text-gray-600">
            {t('error.unauthorized')}
          </p>
        </div>
      </div>
    );
  }

  // If processing a lead (from leads module), show Lead Processing Page
  if (processingLeadId) {
    if (!canEditLeads) {
      setProcessingLeadId(null);
      setError(t('error.unauthorized'));
      return null;
    }
    return (
      <LeadProcessingPage
        leadId={processingLeadId}
        onBack={handleBackFromEdit}
        onSave={handleBackFromEdit}
        currentUser={currentUser}
        onCallEnd={() => setActiveCallLeadId(null)}
        onTakeNextLead={handleTakeNextLead}
      />
    );
  }

  // If editing an order (without leadId), show the order edit page
  if (editingLeadId) {
    if (!canEditLeads) {
      setEditingLeadId(null);
      setError(t('error.unauthorized'));
      return null;
    }
    return (
      <LeadEditPage
        orderId={editingLeadId}
        onBack={handleBackFromEdit}
        onSave={handleBackFromEdit}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('callCenter.title')}
        description={t('callCenter.title')}
        icon={Phone}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => {
          setActiveTab(tabId as TabType);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", tabId);
            return next;
          }, { replace: true });
        }}
        customButtons={customButtons}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="inline-flex text-red-400 hover:text-red-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content — same width and horizontal padding as PageHeader */}
      <div className="w-full lg:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow">
        {activeTab === "new-leads" && canViewNewLeads && (
          <NewLeadsPool
            orders={newLeads}
            totalCount={newLeadsTotal}
            onRefresh={handleRefresh}
            onFetchLeads={fetchNewLeads}
            currentUser={currentUser}
            onTakeNextFromPool={canTakeLeads ? handleTakeNextFromPool : undefined}
            hasActiveCall={!!activeCallLeadId}
            operators={newLeadsOperators}
          />
        )}

        {activeTab === "my-leads" && canViewMyLeads && (
          <MyLeads
            orders={myLeads}
            totalCount={myLeadsTotal}
            onRefresh={handleRefresh}
            refreshing={refreshing}
            onFetchLeads={fetchMyLeads}
            currentUser={currentUser}
            onEditLead={canEditLeads ? handleEditLead : undefined}
          />
        )}

        {activeTab === "my-leads-all" && canViewMyLeads && (
          <MyLeads
            orders={myLeads}
            totalCount={myLeadsTotal}
            onRefresh={handleRefresh}
            refreshing={refreshing}
            onFetchLeads={fetchMyLeads}
            currentUser={currentUser}
            onEditLead={canEditLeads ? handleEditLead : undefined}
          />
        )}

        {/* "Leads" and "Next Call" tabs are hidden */}
        {/* Fallback if user doesn't have permission for active tab */}
        {((activeTab === "new-leads" && !canViewNewLeads) ||
          (activeTab === "my-leads" && !canViewMyLeads) ||
          (activeTab === "my-leads-all" && !canViewMyLeads)) && (
          <div className="p-12 text-center">
            <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('error.forbidden')}
            </h3>
            <p className="text-gray-600">
              {t('error.unauthorized')}
            </p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default CallCenterMain;

